import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../hooks';
import ReactMarkdown from 'react-markdown';
import {
  getClientProposal,
  verifyProposalEmail,
  addClientComment,
  deleteClientComment,
  approveProposal,
  rejectProposal,
} from '../../api/publicProposals';
import ProposalChatPanel from '../../components/proposal/ProposalChatPanel';
import LiveKitAudioPanel from '../../components/proposal/LiveKitAudioPanel';
import useLiveKitAudioV1 from '../../hooks/useLiveKitAudioV1';
import InactiveProposalBanner from '../../components/proposal/InactiveProposalBanner';

/* ─────────────────────────────────────────────────────
   Client Proposal Review Page — Dark Premium Deck
   Inspired by deck.oroswap.org: dark bg #0A0A0A,
   gold #D4A843 accents, warm amber/gold glows,
   glass-dark cards, generous rounded corners.
   ───────────────────────────────────────────────────── */

/* Brand tokens (Dark Gold Premium) */
const Z = {
  bg: '#0A0A0A',
  bgCard: '#111111',
  bgCardHover: '#1A1A1A',
  gold: '#D4A843',
  goldLight: '#E8C97A',
  goldDark: '#B8912E',
  white: '#FFFFFF',
  gray100: '#F5F5F5',
  gray400: '#9CA3AF',
  gray600: '#6B7280',
  gray800: '#1F1F1F',
  border: 'rgba(212,168,67,0.15)',
  borderSubtle: 'rgba(255,255,255,0.06)',
  green: '#059669',
  amber: '#D97706',
  coral: '#EF4444',
};

/* ── Helpers ── */

const getSectionPill = (title) => {
  if (!title) return 'Section';
  const lower = title.toLowerCase();
  if (lower.includes('overview') || lower.includes('summary')) return 'Overview';
  if (lower.includes('market')) return 'Market';
  if (lower.includes('financial') || lower.includes('finance')) return 'Financials';
  if (lower.includes('team')) return 'Team';
  if (lower.includes('risk')) return 'Risk';
  if (lower.includes('structure')) return 'Structure';
  if (lower.includes('term')) return 'Terms';
  if (lower.includes('timeline') || lower.includes('milestones')) return 'Timeline';
  if (lower.includes('recommendation')) return 'Recommendation';
  if (lower.includes('appendix') || lower.includes('appendices')) return 'Appendix';
  return title.split(' ').slice(0, 2).join(' ');
};

/* Splits a heading so the last word is gold-accented */
const AccentHeading = ({ text, className = '', style = {} }) => {
  if (!text) return null;
  const words = text.split(' ');
  if (words.length <= 1) return <span className={className} style={style}>{text}</span>;
  const lead = words.slice(0, -1).join(' ') + ' ';
  const last = words[words.length - 1];
  return (
    <span className={className} style={style}>
      {lead}<span style={{ color: '#D4AF37' }}>{last}</span>
    </span>
  );
};

/* Markdown renderer using react-markdown */
const mdComponents = {
  p: ({ children }) => {
    return <span className="block mb-1 last:mb-0">{children}</span>;
  },
  strong: ({ children }) => <strong style={{ color: Z.white, fontWeight: 600 }}>{children}</strong>,
  em: ({ children }) => <em style={{ color: Z.goldLight }}>{children}</em>,
  ul: ({ children }) => <ul className="mt-1">{children}</ul>,
  ol: ({ children }) => <ol className="mt-1 list-decimal list-inside">{children}</ol>,
  li: ({ children }) => (
    <li className="flex items-start gap-1.5 mb-0.5">
      <span className="w-1 h-1 rounded-full mt-[7px] shrink-0" style={{ background: Z.gold }} />
      <span className="[&>span]:mb-0 [&>span]:inline">{children}</span>
    </li>
  ),
  h3: ({ children }) => <h3 className="text-lg font-semibold mb-2" style={{ color: Z.white }}>{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold mb-1" style={{ color: Z.white }}>{children}</h4>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: Z.gold }}>{children}</a>,
};
const Md = ({ children }) => {
  if (!children || typeof children !== 'string') return children || null;
  return <ReactMarkdown components={mdComponents}>{children}</ReactMarkdown>;
};

const entrance = (active, delay = 0, direction = 'up') => {
  const t = {
    up: 'translateY(40px)',
    down: 'translateY(-30px)',
    left: 'translateX(-50px)',
    right: 'translateX(50px)',
    scale: 'scale(0.93)',
  };
  return {
    opacity: active ? 1 : 0,
    transform: active ? 'none' : t[direction],
    transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
  };
};

/* ── Subsection layout detection ── */
const detectSubsectionLayout = (subsections) => {
  if (!subsections || subsections.length === 0) return 'cards';
  const hasBullets = subsections.some((s) => s.bullet_points && s.bullet_points.length > 0);
  if (hasBullets) return 'cards';
  const contents = subsections.filter((s) => s.content).map((s) => s.content.length);
  const avgLen = contents.length > 0 ? contents.reduce((a, b) => a + b, 0) / contents.length : 0;
  if (subsections.length >= 6 && avgLen < 80) return 'table';
  return 'role';
};

/* ── Visual sub-components ── */

/* Floating particles — gold/amber tones on dark */
const Particles = React.memo(() => {
  const particles = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      size: 2 + Math.random() * 3,
      x: Math.random() * 100,
      y: Math.random() * 100,
      dur: 18 + Math.random() * 25,
      delay: Math.random() * -15,
      opacity: 0.12 + Math.random() * 0.2,
    })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.x}%`,
            top: `${p.y}%`,
            opacity: p.opacity,
            background: p.id % 3 === 0 ? Z.gold : p.id % 3 === 1 ? Z.goldLight : Z.goldDark,
            animation: `particleDrift${p.id % 4} ${p.dur}s ease-in-out ${p.delay}s infinite`,
            boxShadow: `0 0 ${p.size * 4}px ${p.id % 3 === 0 ? 'rgba(212,168,67,0.3)' : 'rgba(184,145,46,0.2)'}`,
          }}
        />
      ))}
    </div>
  );
});
Particles.displayName = 'Particles';

/* Aurora blobs — warm golden washes on dark */
const AuroraBlobs = React.memo(({ variant = 'default' }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div
      className="absolute rounded-full"
      style={{
        width: '55vw', height: '55vw', maxWidth: 750, maxHeight: 750,
        top: '-20%', left: '-15%',
        background: variant === 'accent'
          ? 'radial-gradient(circle, rgba(212,168,67,0.08) 0%, transparent 70%)'
          : 'radial-gradient(circle, rgba(212,168,67,0.05) 0%, transparent 70%)',
        filter: 'blur(80px)',
        animation: 'auroraA 25s ease-in-out infinite',
      }}
    />
    <div
      className="absolute rounded-full"
      style={{
        width: '45vw', height: '45vw', maxWidth: 600, maxHeight: 600,
        bottom: '-15%', right: '-10%',
        background: 'radial-gradient(circle, rgba(212,168,67,0.06) 0%, transparent 70%)',
        filter: 'blur(80px)',
        animation: 'auroraB 30s ease-in-out infinite',
      }}
    />
    <div
      className="absolute rounded-full"
      style={{
        width: '30vw', height: '30vw', maxWidth: 400, maxHeight: 400,
        top: '35%', left: '55%',
        background: 'radial-gradient(circle, rgba(212,168,67,0.04) 0%, transparent 70%)',
        filter: 'blur(60px)',
        animation: 'auroraC 20s ease-in-out infinite',
      }}
    />
  </div>
));
AuroraBlobs.displayName = 'AuroraBlobs';

/* Pulsing orb — gold core on dark */
const Orb = React.memo(({ size = 100 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <div className="absolute inset-0 rounded-full"
      style={{
        background: `radial-gradient(circle, ${Z.gold} 0%, rgba(212,168,67,0.3) 50%, transparent 70%)`,
        animation: 'orbBreathe 4s ease-in-out infinite',
        boxShadow: `0 0 40px rgba(212,168,67,0.35), 0 0 80px rgba(212,168,67,0.15)`,
      }}
    />
    <div className="absolute inset-[-15%] rounded-full border"
      style={{ borderColor: 'rgba(212,168,67,0.25)', animation: 'orbRing 8s linear infinite' }} />
    <div className="absolute inset-[-35%] rounded-full border"
      style={{ borderColor: 'rgba(212,168,67,0.1)', animation: 'orbRing 13s linear infinite reverse' }} />
    <div className="absolute inset-[-55%] rounded-full border"
      style={{ borderColor: 'rgba(212,168,67,0.06)', animation: 'orbRing 20s linear infinite' }} />
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="absolute inset-0" style={{ animation: `orbRing ${7 + i * 2.5}s linear infinite ${i % 2 === 0 ? '' : 'reverse'}` }}>
        <div className="absolute rounded-full"
          style={{
            width: 3, height: 3,
            top: '50%',
            left: i % 2 === 0 ? `${-20 - i * 14}%` : `${120 + i * 10}%`,
            background: i % 2 === 0 ? Z.gold : Z.goldDark,
            boxShadow: `0 0 8px ${i % 2 === 0 ? 'rgba(212,168,67,0.7)' : 'rgba(184,145,46,0.5)'}`,
          }}
        />
      </div>
    ))}
  </div>
));
Orb.displayName = 'Orb';

/* Per-slide background layers with logo icon watermark */
const LogoWatermark = React.memo(({ size = '60vw', top, right, bottom, left, opacity = 0.03, rotate = 0, blur = 0 }) => (
  <img src="/images/logo-icon.svg" alt="" className="absolute pointer-events-none select-none"
    style={{
      width: size, height: 'auto', maxWidth: 900,
      top, right, bottom, left,
      opacity,
      transform: `rotate(${rotate}deg)`,
      filter: `blur(${blur}px) brightness(1.2) saturate(0.5) sepia(1) hue-rotate(5deg)`,
      mixBlendMode: 'lighten',
    }}
  />
));
LogoWatermark.displayName = 'LogoWatermark';

const SlideBg = React.memo(({ variant = 'default' }) => {
  const layers = {
    // Hero: large centered logo behind content + warm glow
    hero: (
      <>
        <LogoWatermark size="50vw" top="5%" right="-5%" opacity={0.04} rotate={10} blur={1} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(212,168,67,0.07) 0%, transparent 60%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 80% 20%, rgba(120,53,15,0.06) 0%, transparent 50%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: '32px 32px' }} />
      </>
    ),
    // Summary: logo top-left faded + warm wash
    summary: (
      <>
        <LogoWatermark size="40vw" top="-10%" left="-8%" opacity={0.025} rotate={-15} blur={2} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 100% 0%, rgba(41,37,36,0.4) 0%, rgba(15,13,11,0.2) 40%, transparent 70%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(212,168,67,0.04) 0%, transparent 50%)' }} />
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: '28px 28px' }} />
      </>
    ),
    // Section even: logo bottom-right + gold bottom wash
    sectionA: (
      <>
        <LogoWatermark size="45vw" bottom="-12%" right="-10%" opacity={0.03} rotate={20} blur={1} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 40%, rgba(28,25,23,0.3) 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{ background: 'linear-gradient(to top, rgba(212,175,55,0.06) 0%, rgba(212,175,55,0.02) 40%, transparent 100%)' }} />
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: '24px 24px' }} />
      </>
    ),
    // Section odd: logo top-left + amber mist
    sectionB: (
      <>
        <LogoWatermark size="42vw" top="-8%" left="-6%" opacity={0.025} rotate={-10} blur={2} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(245,158,11,0.05) 0%, transparent 50%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 100% 100%, rgba(41,37,36,0.35) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "radial-gradient(rgba(212,175,55,0.6) 1px, transparent 1px)", backgroundSize: '30px 30px' }} />
      </>
    ),
    // Decision: centered logo spotlight
    decision: (
      <>
        <LogoWatermark size="55vw" top="10%" left="22%" opacity={0.035} rotate={0} blur={1} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(212,175,55,0.08) 0%, transparent 50%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(120,53,15,0.06) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)", backgroundSize: '28px 28px' }} />
      </>
    ),
  };
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {layers[variant] || layers.sectionA}
    </div>
  );
});
SlideBg.displayName = 'SlideBg';

/* Dark card with subtle border + optional animated gold border glow */
const Card = ({ children, className = '', style = {}, glow = false }) => (
  <div className={`relative group ${className}`} style={style}>
    {glow && (
      <div className="absolute -inset-px rounded-[26px] opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `linear-gradient(135deg, rgba(212,168,67,0.25), rgba(184,145,46,0.2), rgba(232,201,122,0.15))`,
          backgroundSize: '300% 300%',
          animation: 'borderFlow 4s ease infinite',
          filter: 'blur(1px)',
        }}
      />
    )}
    <div className="relative rounded-[26px] overflow-hidden"
      style={{ background: Z.bgCard, border: `1px solid ${Z.borderSubtle}`, boxShadow: '0 6px 24px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)' }}>
      {children}
    </div>
  </div>
);

/* ── Hybrid subsection renderer ── */
const SubsectionRenderer = ({ subsections, isActive, baseDelay = 0.3, className = '' }) => {
  const layout = useMemo(() => detectSubsectionLayout(subsections), [subsections]);
  if (!subsections || subsections.length === 0) return null;

  /* ── TABLE: open layout, gold values, no box ── */
  if (layout === 'table') {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0 ${className}`}>
        {subsections.map((sub, si) => (
          <div key={si}
            className="flex items-baseline justify-between py-2 md:py-3 gap-3 md:gap-4"
            style={{
              ...entrance(isActive, baseDelay + 0.05 + si * 0.03, 'up'),
              borderBottom: `1px solid ${Z.borderSubtle}`,
            }}>
            <span className="text-[10px] md:text-xs font-medium uppercase tracking-wider shrink-0" style={{ color: Z.gray400 }}>
              {sub.subtitle}
            </span>
            <span className="text-xs md:text-sm font-semibold text-right" style={{ color: Z.gold }}>
              {sub.content}
            </span>
          </div>
        ))}
      </div>
    );
  }

  /* ── ROLE: clean left-border with gold badge ── */
  if (layout === 'role') {
    return (
      <div className={`grid gap-x-4 gap-y-6 md:gap-y-10 ${subsections.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${className}`}>
        {subsections.map((sub, si) => (
          <div key={si}
            className="pl-4 md:pl-5"
            style={{
              ...entrance(isActive, baseDelay + si * 0.08, si % 2 === 0 ? 'left' : 'right'),
              borderLeft: `3px solid ${Z.gold}`,
            }}>
            {sub.subtitle && (
              <span className="inline-block text-[10px] md:text-xs font-semibold uppercase tracking-wider px-2 py-0.5 md:px-2.5 md:py-1 rounded-md mb-2 md:mb-3"
                style={{ color: Z.gold, background: 'rgba(212,168,67,0.1)', border: `1px solid ${Z.border}` }}>
                {sub.subtitle}
              </span>
            )}
            {sub.content && (
              <div className="text-xs md:text-sm leading-relaxed" style={{ color: Z.gray400 }}>
                <Md>{sub.content}</Md>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  /* ── CARDS: default — subtitle + bullets, clean left-border style ── */
  return (
    <div className={`grid gap-x-4 gap-y-6 md:gap-y-10 ${subsections.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : subsections.length === 2 ? 'md:grid-cols-2' : 'grid-cols-1'} ${className}`}>
      {subsections.map((sub, si) => (
        <div key={si}
          className="pl-4 md:pl-5"
          style={{
            ...entrance(isActive, baseDelay + si * 0.06, 'up'),
            borderLeft: `3px solid ${Z.gold}`,
          }}>
          {sub.subtitle && (
            <span className="inline-block text-[10px] md:text-xs font-semibold uppercase tracking-wider px-2 py-0.5 md:px-2.5 md:py-1 rounded-md mb-2 md:mb-3"
              style={{ color: Z.gold, background: 'rgba(212,168,67,0.1)', border: `1px solid ${Z.border}` }}>
              {sub.subtitle}
            </span>
          )}
          {sub.content && (
            <div className="text-xs md:text-sm leading-relaxed" style={{ color: Z.gray400 }}><Md>{sub.content}</Md></div>
          )}
          {sub.bullet_points && (
            <ul className="mt-1 space-y-1">
              {sub.bullet_points.map((bp, bi) => (
                <li key={bi} className="text-xs md:text-sm flex items-start gap-1.5 md:gap-2" style={{ color: Z.gray400 }}>
                  <span className="text-sm md:text-base leading-none mt-0.5 shrink-0" style={{ color: Z.gold }}>•</span>
                  {bp}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

/* Ghost section number — very faint white */
const GhostNumber = ({ number, isActive }) => (
  <div className="absolute -top-6 -right-2 md:right-6 select-none pointer-events-none z-0"
    style={entrance(isActive, 0, 'right')}>
    <span className="text-[100px] md:text-[160px] lg:text-[220px] font-extrabold leading-none"
      style={{ color: 'rgba(255,255,255,0.03)' }}>
      {String(number).padStart(2, '0')}
    </span>
  </div>
);

/* Gold dashed line */
const DashedLine = ({ vertical = false, className = '' }) => (
  <div className={className}
    style={{
      [vertical ? 'width' : 'height']: '1px',
      background: vertical
        ? `repeating-linear-gradient(to bottom, ${Z.gold} 0px, ${Z.gold} 6px, transparent 6px, transparent 12px)`
        : `repeating-linear-gradient(to right, ${Z.gold} 0px, ${Z.gold} 6px, transparent 6px, transparent 12px)`,
    }}
  />
);

/* ════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════ */

const ClientProposalReviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, loading: authLoading } = useAuth();

  const [emailVerified, setEmailVerified] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);

  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [commentDrawerSection, setCommentDrawerSection] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [addingComment, setAddingComment] = useState(null);
  const [deletingComment, setDeletingComment] = useState(null);

  const [showDecisionDialog, setShowDecisionDialog] = useState(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [presenterActive, setPresenterActive] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const livekit = useLiveKitAudioV1();

  /* ── Email ── */

  useEffect(() => {
    if (authLoading) return;
    if (user) { setEmailVerified(true); return; }
    const stored = localStorage.getItem(`proposal_verified_${token}`);
    if (stored) setEmailVerified(true);
  }, [token, user, authLoading]);

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setVerifying(true);
    setEmailError('');
    try {
      const res = await verifyProposalEmail(token, { email: emailInput.trim() });
      if (res.data?.verified) {
        localStorage.setItem(`proposal_verified_${token}`, emailInput.trim());
        setEmailVerified(true);
      } else {
        setEmailError('Email does not match our records');
      }
    } catch (err) {
      setEmailError(err?.response?.data?.detail || 'Unable to verify email');
    } finally {
      setVerifying(false);
    }
  };

  /* ── Data ── */

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await getClientProposal(token);
      setData(res.data);
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load proposal');
      } else {
        console.error('Silent fetchData background refresh failed:', err);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (emailVerified) fetchData();
  }, [emailVerified, fetchData]);

  /* ── Navigation ── */

  const goToSlide = useCallback((index, totalSlides, instant = false, fromAgent = false) => {
    // Block manual slide navigation while presenter is reading (allow agent-triggered advances)
    if (!fromAgent && livekit.slidesLocked) return;
    const clamped = Math.max(0, Math.min(index, totalSlides - 1));
    const container = containerRef.current;
    if (container) container.scrollTo({ left: clamped * container.clientWidth, behavior: instant ? 'instant' : 'smooth' });
  }, [livekit.slidesLocked]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Reset to first slide on mount (prevent browser scroll restoration)
    container.scrollTo({ left: 0, behavior: 'instant' });
    setCurrentSlide(0);
    const h = () => {
      const w = container.clientWidth;
      if (w > 0) setCurrentSlide(Math.round(container.scrollLeft / w));
    };
    container.addEventListener('scroll', h, { passive: true });
    return () => container.removeEventListener('scroll', h);
  }, [data]);


  /* ── Comments ── */

  const openComments = (n) => { setCommentDrawerSection(n); setCommentDrawerOpen(true); };

  const handleAddComment = async (n) => {
    const text = commentInputs[n]?.trim();
    if (!text) return;
    setAddingComment(n);
    try {
      await addClientComment(token, { section_number: n, comment: text });
      setCommentInputs((p) => ({ ...p, [n]: '' }));
      await fetchData(true);
    } catch { /* ignore */ }
    setAddingComment(null);
  };

  const handleDeleteComment = async (id) => {
    setDeletingComment(id);
    try { await deleteClientComment(token, id); await fetchData(true); } catch { /* */ }
    setDeletingComment(null);
  };

  /* ── Decision ── */

  const handleDecision = async (type) => {
    if (decisionComment.trim().length < 20) return;
    setSubmittingDecision(true);
    try {
      await (type === 'approve' ? approveProposal : rejectProposal)(token, { comment: decisionComment.trim() });
      setShowDecisionDialog(null);
      setDecisionComment('');
      setSuccessMessage(
        type === 'approve'
          ? 'Your acceptance has been sent to the Zamanat team. Thank you!'
          : 'Your feedback has been sent to the Zamanat team. We will review your comments and get back to you shortly.'
      );
      setTimeout(() => setSuccessMessage(null), 8000);
      await fetchData(true);
    } catch { /* */ }
    setSubmittingDecision(false);
  };

  /* ── Styles ── */

  const globalStyles = `
    @keyframes particleDrift0 { 0%,100%{transform:translate(0,0)} 25%{transform:translate(30px,-40px)} 50%{transform:translate(-20px,-80px)} 75%{transform:translate(15px,-30px)} }
    @keyframes particleDrift1 { 0%,100%{transform:translate(0,0)} 25%{transform:translate(-25px,35px)} 50%{transform:translate(40px,-20px)} 75%{transform:translate(-10px,50px)} }
    @keyframes particleDrift2 { 0%,100%{transform:translate(0,0)} 25%{transform:translate(50px,20px)} 50%{transform:translate(-30px,60px)} 75%{transform:translate(20px,-40px)} }
    @keyframes particleDrift3 { 0%,100%{transform:translate(0,0)} 25%{transform:translate(-40px,-30px)} 50%{transform:translate(25px,45px)} 75%{transform:translate(-35px,15px)} }
    @keyframes auroraA { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(60px,40px) scale(1.1)} 66%{transform:translate(-30px,80px) scale(0.95)} }
    @keyframes auroraB { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-50px,-60px) scale(1.15)} 66%{transform:translate(40px,-30px) scale(0.9)} }
    @keyframes auroraC { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(70px,-50px) scale(1.2)} }
    @keyframes orbBreathe { 0%,100%{transform:scale(1);opacity:0.8} 50%{transform:scale(1.08);opacity:1} }
    @keyframes orbRing { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
    @keyframes borderFlow { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
    @keyframes pulseGentle { 0%,100%{opacity:0.3} 50%{opacity:1} }
    @keyframes gradientShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
    @keyframes heroLine { 0%{transform:translateY(-24px);opacity:0} 15%{opacity:0.8} 50%{transform:translateY(24px);opacity:0.8} 85%{opacity:0.8} 100%{transform:translateY(-24px);opacity:0} }
    @keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
    @keyframes glowPulse { 0%,100%{box-shadow:0 4px 20px rgba(212,168,67,0.15)} 50%{box-shadow:0 4px 30px rgba(212,168,67,0.35),0 0 60px rgba(212,168,67,0.1)} }
    @keyframes slideInRight { from{transform:translateX(100%)} to{transform:translateX(0)} }
    @keyframes menuFadeIn { from{opacity:0;transform:scale(0.96) translateY(-8px)} to{opacity:1;transform:scale(1) translateY(0)} }
    .text-shimmer-gold {
      background: linear-gradient(90deg, ${Z.gold}, ${Z.goldLight}, ${Z.gold});
      background-size: 200% 100%;
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text; animation: gradientShift 5s ease-in-out infinite;
    }
    .dot-pattern-dark {
      background-image: radial-gradient(rgba(255,255,255,0.015) 1px, transparent 1px);
      background-size: 32px 32px;
    }
    .deck-scroll::-webkit-scrollbar { display: none; }
    .deck-scroll { -ms-overflow-style: none; scrollbar-width: none; }
    p:has(> ul), p:has(> ol) { white-space: normal !important; }
    p > ul, p > ol { margin-top: 2px !important; margin-bottom: 0 !important; }
    ul > li, ol > li { margin-bottom: 1px !important; }
  `;

  /* ════════════════════════════════════════════════════
     EMAIL GATE
     ════════════════════════════════════════════════════ */

  if (!emailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #0F0D08 50%, #0A0A0A 100%)' }}>
        <style>{globalStyles}</style>
        <AuroraBlobs />
        <Particles />
        <div className="absolute inset-0 dot-pattern-dark pointer-events-none" />

        <div className="relative z-20 max-w-md w-full">
          <div className="flex justify-center mb-8" style={{ animation: 'floatY 6s ease-in-out infinite' }}>
            <Orb size={70} />
          </div>

          <Card glow>
            <div className="p-8 sm:p-10">
              <div className="text-center mb-8">
                <img src="/images/logo.svg" alt="Zamanat" className="h-8 mx-auto mb-6 brightness-0 invert" />
                <h1 className="text-2xl font-bold mb-2" style={{ color: Z.white }}>Access Proposal</h1>
                <p className="text-sm" style={{ color: Z.gray400 }}>Enter your email to view this proposal.</p>
              </div>

              <form onSubmit={handleVerifyEmail}>
                <label className="block text-[11px] font-bold uppercase tracking-[0.15em] mb-2" style={{ color: Z.gold }}>
                  Email Address
                </label>
                <input
                  type="email" value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                  placeholder="you@company.com" required autoFocus
                  className="w-full rounded-xl px-4 py-3.5 text-sm focus:outline-none transition-all"
                  style={{
                    background: Z.bgCardHover,
                    border: `1px solid ${Z.borderSubtle}`,
                    color: Z.white,
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = Z.gold; e.target.style.boxShadow = `0 0 0 3px rgba(212,168,67,0.15), inset 0 1px 2px rgba(0,0,0,0.2)`; }}
                  onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.2)'; }}
                />
                {emailError && <p className="text-sm mt-2" style={{ color: Z.coral }}>{emailError}</p>}
                <button
                  type="submit" disabled={verifying || !emailInput.trim()}
                  className="mt-5 w-full py-3.5 text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-40 transition-all"
                  style={{
                    background: Z.gold,
                    color: '#000000',
                    border: `1px solid ${Z.goldLight}`,
                    animation: !verifying ? 'glowPulse 3s ease-in-out infinite' : 'none',
                  }}
                >
                  {verifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-black/20 border-t-black" />
                      Verifying...
                    </span>
                  ) : 'Continue'}
                </button>
              </form>
              <p className="text-[11px] text-center mt-6" style={{ color: Z.gray600 }}>
                Use the email address associated with this proposal.
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════
     LOADING
     ════════════════════════════════════════════════════ */

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #0F0D08 50%, #0A0A0A 100%)' }}>
        <style>{globalStyles}</style>
        <AuroraBlobs />
        <div className="relative z-10" style={{ animation: 'floatY 3s ease-in-out infinite' }}>
          <Orb size={60} />
        </div>
        <p className="mt-6 text-xs uppercase tracking-[0.2em] relative z-10"
          style={{ color: 'rgba(212,168,67,0.4)', animation: 'pulseGentle 2s ease-in-out infinite' }}>
          Loading proposal
        </p>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════
     ERROR
     ════════════════════════════════════════════════════ */

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #0F0D08 50%, #0A0A0A 100%)' }}>
        <style>{globalStyles}</style>
        <AuroraBlobs />
        <div className="relative z-10 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.08)', border: `1px solid rgba(239,68,68,0.15)` }}>
            <svg className="w-8 h-8" style={{ color: Z.coral }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: Z.white }}>Unable to Load Proposal</h2>
          <p style={{ color: Z.gray400 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  /* ════════════════════════════════════════════════════
     DATA
     ════════════════════════════════════════════════════ */

  const { proposal, client_comments, is_decided, client_decision, client_decision_comment, company_name } = data;
  const isInactive = data && data.is_active === false;
  const sections = proposal.sections || [];

  const commentsBySection = {};
  (client_comments || []).forEach((c) => {
    if (!commentsBySection[c.section_number]) commentsBySection[c.section_number] = [];
    commentsBySection[c.section_number].push(c);
  });

  const slides = [];
  slides.push({ type: 'hero' });
  sections.forEach((section, idx) => {
    slides.push({ type: 'section', section, idx, sectionNumber: section.section_number || idx + 1 });
  });
  slides.push({ type: 'decision' });
  const totalSlides = slides.length;
  const progress = totalSlides > 1 ? currentSlide / (totalSlides - 1) : 0;

  const drawerComments = commentDrawerSection ? (commentsBySection[commentDrawerSection] || []) : [];
  const drawerSectionTitle = commentDrawerSection
    ? (sections.find((s) => (s.section_number || sections.indexOf(s) + 1) === commentDrawerSection)?.title || `Section ${commentDrawerSection}`)
    : '';

  /* ════════════════════════════════════════════════════
     DECK
     ════════════════════════════════════════════════════ */

  return (
    <div className="h-screen w-screen overflow-hidden relative select-none" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #0F0D08 50%, #0A0A0A 100%)' }}>
      <style>{globalStyles}</style>

      {/* Global background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 dot-pattern-dark" />
      </div>

      {/* Progress bar — gold gradient on left edge */}
      <div className="fixed top-0 left-0 bottom-0 w-[3px] z-40" style={{ background: 'rgba(255,255,255,0.03)' }}>
        <div className="w-full rounded-b transition-all duration-700 ease-out"
          style={{
            height: `${progress * 100}%`,
            background: `linear-gradient(180deg, ${Z.gold}, ${Z.goldDark})`,
            boxShadow: `0 0 8px rgba(212,168,67,0.4)`,
          }}
        />
      </div>

      {/* Slide counter */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
        <div className="bg-black/60 backdrop-blur-xl rounded-full px-5 py-2 flex items-center gap-3"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)' }}>
          <span className="text-sm font-mono font-bold" style={{ color: Z.gold }}>{String(currentSlide + 1).padStart(2, '0')}</span>
          <div className="w-16 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress * 100}%`, background: `linear-gradient(90deg, ${Z.gold}, ${Z.goldLight})` }} />
          </div>
          <span className="text-sm font-mono" style={{ color: Z.gray600 }}>{String(totalSlides).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Nav arrows */}
      <button
        onClick={() => goToSlide(currentSlide - 1, totalSlides)}
        disabled={currentSlide === 0}
        className="fixed left-2 md:left-6 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-xl disabled:opacity-0 disabled:pointer-events-none transition-all duration-500 hover:scale-105"
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)', color: Z.white }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={() => goToSlide(currentSlide + 1, totalSlides)}
        disabled={currentSlide === totalSlides - 1}
        className="fixed right-2 md:right-6 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-xl disabled:opacity-0 disabled:pointer-events-none transition-all duration-500 hover:scale-105"
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)', color: Z.white }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Top bar backdrop — hides scrolling content behind header */}
      <div className="fixed top-0 left-0 right-0 z-30 h-14 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,1) 0%, rgba(10,10,10,0.95) 70%, rgba(10,10,10,0) 100%)' }} />

      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 md:px-8 py-4 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-4">
          <img src="/images/logo.svg" alt="Zamanat" className="h-6 hover:opacity-80 transition-opacity brightness-0 invert" />
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <button onClick={() => setMenuOpen(true)} title="Menu"
            className="w-10 h-10 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-xl transition-all duration-300 hover:scale-105"
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)', color: Z.white }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <KeyboardNav currentSlide={currentSlide} totalSlides={totalSlides} goToSlide={goToSlide} />

      {/* Inactive proposal banner — fixed at top */}
      {isInactive && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <InactiveProposalBanner activeVersionUrl={data.active_version_url} />
        </div>
      )}

      {/* ═══ SLIDE CONTAINER ═══ */}
      <div ref={containerRef} className="deck-scroll h-full w-full overflow-x-scroll overflow-y-hidden relative z-10"
        style={{ scrollSnapType: 'x mandatory' }}>
        <div className="flex h-full" style={{ width: `${totalSlides * 100}vw` }}>

          {slides.map((slide, slideIdx) => {
            const isActive = currentSlide === slideIdx;

            /* ── HERO ── */
            if (slide.type === 'hero') {
              return (
                <section key="hero" className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center justify-center overflow-hidden"
                  style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
                  <SlideBg variant="hero" />
                  <Particles />


                  <div className="relative z-10 text-center max-w-4xl px-6 select-text">
                    <div className="flex justify-center mb-10" style={{ ...entrance(isActive, 0.05, 'scale'), animation: isActive ? 'floatY 6s ease-in-out infinite' : 'none' }}>
                      <Orb size={100} />
                    </div>

                    {company_name && (
                      <p className="text-sm font-bold uppercase tracking-[0.3em] mb-5" style={entrance(isActive, 0.15, 'down')}>
                        <span className="text-shimmer-gold">{company_name}</span>
                      </p>
                    )}

                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-medium leading-[0.92] mb-4 tracking-tight"
                      style={{ ...entrance(isActive, 0.25, 'up'), color: Z.white, fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.03em' }}>
                      <AccentHeading text={proposal.title || 'Investment Proposal'} />
                    </h1>

                    <div style={entrance(isActive, 0.32, 'scale')} className="mb-6" />

                    <div className="flex items-center justify-center gap-4 text-sm" style={{ ...entrance(isActive, 0.4, 'up'), color: Z.gray400 }}>
                      <span>Version {proposal.version}</span>
                      <span className="w-1 h-1 rounded-full" style={{ background: Z.gold }} />
                      <span>{new Date(proposal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>

                    {is_decided && (
                      <div className="mt-8 flex justify-center" style={entrance(isActive, 0.5, 'up')}>
                        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-md border-2 backdrop-blur-sm"
                          style={{
                            color: client_decision === 'approved' ? Z.green : Z.amber,
                            background: client_decision === 'approved' ? 'rgba(5,150,105,0.15)' : 'rgba(217,119,6,0.15)',
                            borderColor: client_decision === 'approved' ? 'rgba(5,150,105,0.5)' : 'rgba(217,119,6,0.5)',
                          }}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            {client_decision === 'approved'
                              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              : <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                            }
                          </svg>
                          <span className="text-sm font-bold uppercase tracking-[0.15em]">
                            {client_decision === 'approved' ? 'Proposal Accepted' : 'Sent Back for Changes'}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mt-20" style={entrance(isActive, 0.55, 'up')}>
                      <p className="text-[10px] uppercase tracking-[0.25em] mb-3"
                        style={{ color: 'rgba(212,168,67,0.4)', animation: 'pulseGentle 3s ease-in-out infinite' }}>
                        Scroll to explore
                      </p>
                      <div className="mx-auto w-px h-12 overflow-hidden">
                        <div className="w-full h-6" style={{ background: `linear-gradient(180deg, ${Z.gold}, transparent)`, animation: 'heroLine 3s ease-in-out infinite' }} />
                      </div>
                    </div>
                  </div>
                </section>
              );
            }

            /* ── SECTION ── */
            if (slide.type === 'section') {
              const { section, sectionNumber } = slide;
              const sectionCommentCount = (commentsBySection[sectionNumber] || []).length;
              const displayNumber = sectionNumber;
              const hasMetrics = section.key_metrics && section.key_metrics.length > 0;
              const hasSubsections = section.subsections && section.subsections.length > 0;

              return (
                <section key={`section-${sectionNumber}`}
                  className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center overflow-hidden"
                  style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
                  <SlideBg variant={sectionNumber % 2 === 0 ? 'sectionA' : 'sectionB'} />
                  {company_name && (
                    <div className="absolute top-12 md:top-7 left-0 right-0 z-20 text-center pointer-events-none" style={entrance(isActive, 0.1, 'down')}>
                      <div className="py-2 md:py-1.5"
                        style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.8) 60%, rgba(10,10,10,0) 100%)' }}>
                        <span className="text-[11px] md:text-sm font-bold uppercase tracking-[0.3em] text-shimmer-gold">{company_name}</span>
                      </div>
                    </div>
                  )}
                  <GhostNumber number={displayNumber} isActive={isActive} />

                  <div className="relative z-10 w-full max-w-7xl mx-auto px-14 md:px-16 lg:px-20 h-full flex flex-col pt-20 pb-20 md:pt-24 md:pb-24 overflow-y-auto deck-scroll select-text">
                    <div className="my-auto">

                    {/* ── Two-column: heading left, metrics right ── */}
                    {hasMetrics ? (
                      <>
                        <div className="flex items-start justify-end mb-3 flex-shrink-0">
                          {(!is_decided || sectionCommentCount > 0) && (
                            <button onClick={() => openComments(sectionNumber)}
                              className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-black/40 backdrop-blur-sm transition-all duration-500 hover:scale-105"
                              style={{
                                ...entrance(isActive, 0.15, 'right'),
                                boxShadow: '0 2px 10px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)',
                                color: Z.white,
                              }}>
                              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              <span className="text-xs md:text-sm font-medium">{sectionCommentCount > 0 ? sectionCommentCount : (is_decided ? '' : 'Comment')}</span>
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 flex-shrink-0">
                          {/* Left: heading + brief text */}
                          <div className="flex flex-col justify-start">
                            <h2 className="text-2xl md:text-4xl lg:text-6xl font-medium mb-3 leading-[1.0] tracking-tight"
                              style={{ ...entrance(isActive, 0.15, 'left'), color: Z.white, fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.025em' }}>
                              <AccentHeading text={section.title || `Section ${sectionNumber}`} />
                            </h2>
                            <div style={entrance(isActive, 0.22, 'left')} className="mb-3 md:mb-5" />
                            {section.content && (
                              <div className="text-sm md:text-base lg:text-lg font-light leading-relaxed max-w-lg"
                                style={{ ...entrance(isActive, 0.28, 'left'), color: '#A8A29E', letterSpacing: '0.01em' }}>
                                {typeof section.content === 'string'
                                  ? <Md>{section.content}</Md>
                                  : JSON.stringify(section.content, null, 2)}
                              </div>
                            )}
                          </div>

                          {/* Right: hero metric cards — ORO style: value (gold) → label (white) */}
                          <div className="flex flex-col justify-start">
                            <div className={`grid gap-3 md:gap-4 ${section.key_metrics.length === 3 ? 'grid-cols-1' : section.key_metrics.length <= 2 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                              {section.key_metrics.map((m, mi) => {
                                const vLen = String(m.value).length;
                                const isCompact = section.key_metrics.length === 3;
                                const valueSizeClass = isCompact
                                  ? 'text-base md:text-xl lg:text-2xl'
                                  : vLen > 16 ? 'text-sm md:text-lg lg:text-xl' : vLen > 10 ? 'text-base md:text-xl lg:text-2xl' : vLen > 5 ? 'text-lg md:text-2xl lg:text-3xl' : 'text-xl md:text-3xl lg:text-4xl';
                                return (
                                <div key={mi}
                                  className={`group rounded-xl md:rounded-2xl relative overflow-hidden transition-all duration-500 hover:-translate-y-1 ${isCompact ? 'p-3 md:p-5 flex items-center justify-between gap-3' : 'p-4 md:p-6'}`}
                                  style={{
                                    ...entrance(isActive, 0.2 + mi * 0.08, 'right'),
                                    background: Z.bgCard,
                                    border: `1px solid ${Z.borderSubtle}`,
                                  }}>
                                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                    style={{ background: 'radial-gradient(circle at 30% 80%, rgba(212,168,67,0.1) 0%, transparent 70%)' }} />
                                  <div className="absolute top-0 left-6 right-6 h-[1px]"
                                    style={{ background: `linear-gradient(90deg, transparent, ${Z.border}, transparent)` }} />
                                  {isCompact ? (
                                    <>
                                      <p className="text-xs md:text-sm font-semibold relative z-10" style={{ color: Z.white }}>{m.label}</p>
                                      <p className={`font-bold leading-none relative z-10 ${valueSizeClass}`} style={{ color: Z.gold }}>{m.value}</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className={`font-bold leading-tight relative z-10 mb-2 md:mb-3 ${valueSizeClass}`} style={{ color: Z.gold }}>{m.value}</p>
                                      <p className="text-xs md:text-sm font-semibold relative z-10" style={{ color: Z.white }}>{m.label}</p>
                                    </>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Subsections below if any */}
                        {hasSubsections && (
                          <SubsectionRenderer subsections={section.subsections} isActive={isActive} baseDelay={0.4} className="mt-6 md:mt-8 flex-shrink-0" />
                        )}
                      </>
                    ) : (
                      /* ── No metrics: full-width content layout ── */
                      <>
                        <div className="flex items-start justify-end mb-3 md:mb-4 flex-shrink-0">
                          {(!is_decided || sectionCommentCount > 0) && (
                            <button onClick={() => openComments(sectionNumber)}
                              className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-black/40 backdrop-blur-sm transition-all duration-500 hover:scale-105"
                              style={{
                                ...entrance(isActive, 0.15, 'right'),
                                boxShadow: '0 2px 10px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)',
                                color: Z.white,
                              }}>
                              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              <span className="text-xs md:text-sm font-medium">{sectionCommentCount > 0 ? sectionCommentCount : (is_decided ? '' : 'Comment')}</span>
                            </button>
                          )}
                        </div>

                        <h2 className="text-2xl md:text-4xl lg:text-5xl font-medium mb-2 leading-[1.0] tracking-tight flex-shrink-0"
                          style={{ ...entrance(isActive, 0.2, 'left'), color: Z.white, fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.025em' }}>
                          <AccentHeading text={section.title || `Section ${sectionNumber}`} />
                        </h2>
                        <div style={entrance(isActive, 0.26, 'left')} className="mb-4 md:mb-6 flex-shrink-0" />

                        {section.content && (
                          <div className="pl-4 md:pl-6 mb-4 md:mb-5 flex-shrink-0" style={{ ...entrance(isActive, 0.3, 'left'), borderLeft: `3px solid ${Z.gold}` }}>
                            <div className="leading-relaxed text-sm md:text-base lg:text-lg font-light whitespace-pre-line" style={{ color: '#A8A29E', letterSpacing: '0.01em' }}>
                              {typeof section.content === 'string' ? <Md>{section.content}</Md> : JSON.stringify(section.content, null, 2)}
                            </div>
                          </div>
                        )}

                        {hasSubsections && (
                          <SubsectionRenderer subsections={section.subsections} isActive={isActive} baseDelay={0.3} className="mb-4 md:mb-5 flex-shrink-0" />
                        )}
                      </>
                    )}
                    </div>
                  </div>
                </section>
              );
            }

            /* ── DECISION ── */
            if (slide.type === 'decision') {
              return (
                <section key="decision" className="w-screen min-w-[100vw] h-screen flex-shrink-0 relative flex items-center justify-center overflow-hidden"
                  style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
                  <SlideBg variant="decision" />
                  {company_name && (
                    <div className="absolute top-12 md:top-7 left-0 right-0 z-20 text-center pointer-events-none" style={entrance(isActive, 0.1, 'down')}>
                      <div className="py-2 md:py-1.5"
                        style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.8) 60%, rgba(10,10,10,0) 100%)' }}>
                        <span className="text-[11px] md:text-sm font-bold uppercase tracking-[0.3em] text-shimmer-gold">{company_name}</span>
                      </div>
                    </div>
                  )}
                  <Particles />

                  <div className="relative z-10 text-center max-w-lg px-6 select-text">
                    {is_decided ? (
                      <>
                        <div style={entrance(isActive, 0.1, 'scale')}>
                          <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-8"
                            style={{
                              background: client_decision === 'approved' ? 'rgba(5,150,105,0.1)' : 'rgba(217,119,6,0.1)',
                              border: `2px solid ${client_decision === 'approved' ? 'rgba(5,150,105,0.25)' : 'rgba(217,119,6,0.25)'}`,
                              boxShadow: client_decision === 'approved' ? '0 0 40px rgba(5,150,105,0.15)' : '0 0 40px rgba(217,119,6,0.12)',
                            }}>
                            {client_decision === 'approved' ? (
                              <svg className="w-12 h-12" style={{ color: Z.green }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-12 h-12" style={{ color: Z.amber }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-medium mb-4 leading-[1.0] tracking-tight" style={{ ...entrance(isActive, 0.2, 'up'), color: Z.white, fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.025em' }}>
                          <AccentHeading text={client_decision === 'approved' ? 'Proposal Accepted' : 'Sent Back for Changes'} />
                        </h2>
                        {client_decision_comment && (
                          <div style={entrance(isActive, 0.35, 'up')}>
                            <Card>
                              <div className="p-5 text-left">
                                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: Z.gray600 }}>Your feedback</p>
                                <p className="text-sm leading-relaxed" style={{ color: Z.gray400 }}>{client_decision_comment}</p>
                              </div>
                            </Card>
                          </div>
                        )}
                        {successMessage && (
                          <div className="mt-6 flex items-center gap-2.5 justify-center" style={entrance(isActive, 0.45, 'up')}>
                            <svg className="w-4 h-4 flex-shrink-0" style={{ color: Z.green }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <p className="text-sm" style={{ color: Z.gray400 }}>{successMessage}</p>
                          </div>
                        )}
                      </>
                    ) : isInactive ? (
                      <>
                        <div className="flex justify-center mb-10" style={{ ...entrance(isActive, 0.05, 'scale'), animation: isActive ? 'floatY 5s ease-in-out infinite' : 'none' }}>
                          <Orb size={80} />
                        </div>
                        <div style={entrance(isActive, 0.15, 'down')}>
                          <span className="inline-block text-sm font-medium tracking-widest uppercase rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm"
                            style={{ color: 'rgba(245,158,11,1)', background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.25)' }}>
                            Outdated Version
                          </span>
                        </div>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium mb-4 leading-[1.0] tracking-tight" style={{ ...entrance(isActive, 0.25, 'up'), color: 'rgba(163,163,163,1)', fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.025em' }}>
                          This version is no longer current
                        </h2>
                        <div className="mt-8">
                          <InactiveProposalBanner activeVersionUrl={data.active_version_url} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-center mb-10" style={{ ...entrance(isActive, 0.05, 'scale'), animation: isActive ? 'floatY 5s ease-in-out infinite' : 'none' }}>
                          <Orb size={80} />
                        </div>

                        <div style={entrance(isActive, 0.15, 'down')}>
                          <span className="inline-block text-sm font-medium tracking-widest uppercase rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm"
                            style={{ color: '#FBBF24', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                            Your Decision
                          </span>
                        </div>

                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium mb-4 leading-[1.0] tracking-tight" style={{ ...entrance(isActive, 0.25, 'up'), color: Z.white, fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.025em' }}>
                          <AccentHeading text="Ready to proceed?" />
                        </h2>

                        <div style={entrance(isActive, 0.32, 'up')} className="mb-8" />

                        <p className="mb-12 text-base" style={{ ...entrance(isActive, 0.38, 'up'), color: Z.gray600 }}>
                          Review complete. Submit your decision below.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center gap-4 justify-center" style={entrance(isActive, 0.45, 'up')}>
                          <button
                            onClick={() => { setShowDecisionDialog('approve'); setDecisionComment(''); }}
                            className="w-full sm:w-auto px-12 py-4 text-sm font-semibold rounded-xl hover:opacity-90 transition-all"
                            style={{
                              background: Z.gold,
                              color: '#000000',
                              border: `1px solid ${Z.goldLight}`,
                              boxShadow: '0 4px 20px rgba(212,168,67,0.25), 0 0 40px rgba(212,168,67,0.08)',
                            }}>
                            Accept Proposal
                          </button>
                          <button
                            onClick={() => { setShowDecisionDialog('reject'); setDecisionComment(''); }}
                            className="w-full sm:w-auto px-12 py-4 text-sm font-semibold rounded-xl transition-all duration-500 hover:shadow-lg"
                            style={{
                              color: Z.amber,
                              border: `1px solid rgba(217,119,6,0.25)`,
                              background: 'rgba(217,119,6,0.06)',
                            }}>
                            Send Back
                          </button>
                        </div>

                        {data.expires_at && (
                          <p className="text-[11px] mt-12" style={{ ...entrance(isActive, 0.55, 'up'), color: Z.gray600 }}>
                            Expires {new Date(data.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </section>
              );
            }

            return null;
          })}
        </div>
      </div>

      {/* ═══ OVERLAYS ═══ */}

      {/* Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative rounded-[26px] max-w-sm w-full mx-4 p-6 max-h-[80vh] overflow-y-auto"
            style={{ background: Z.bgCard, border: `1px solid ${Z.borderSubtle}`, animation: 'menuFadeIn 0.25s ease-out', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-bold uppercase tracking-[0.15em]" style={{ color: Z.gold }}>Sections</h3>
              <button onClick={() => setMenuOpen(false)} style={{ color: Z.gray600 }} className="hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="space-y-1">
              {slides.map((s, idx) => {
                const label = s.type === 'hero' ? 'Title' : s.type === 'decision' ? 'Your Decision' : s.section?.title || `Section ${s.sectionNumber}`;
                const cur = currentSlide === idx;
                return (
                  <button key={idx} onClick={() => { goToSlide(idx, totalSlides); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm transition-all duration-300 flex items-center gap-3"
                    style={{
                      color: cur ? Z.white : Z.gray600,
                      background: cur ? 'rgba(212,168,67,0.08)' : 'transparent',
                      border: cur ? `1px solid ${Z.border}` : '1px solid transparent',
                    }}>
                    <span className="text-[10px] font-mono w-5" style={{ color: Z.gray600 }}>{String(idx + 1).padStart(2, '0')}</span>
                    <span className="flex-1 truncate">{label}</span>
                    {cur && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: Z.gold, boxShadow: '0 0 6px rgba(212,168,67,0.5)' }} />}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Comment Drawer */}
      {commentDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setCommentDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm h-full flex flex-col border-l"
            style={{ background: Z.bgCard, borderColor: Z.borderSubtle, animation: 'slideInRight 0.3s cubic-bezier(0.16,1,0.3,1)', boxShadow: '-10px 0 40px rgba(0,0,0,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: Z.borderSubtle }}>
              <div>
                <h3 className="text-sm font-bold" style={{ color: Z.white }}>Comments</h3>
                <p className="text-xs mt-0.5 truncate max-w-[250px]" style={{ color: Z.gray600 }}>{drawerSectionTitle}</p>
              </div>
              <button onClick={() => setCommentDrawerOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-105"
                style={{ color: Z.gray600, background: Z.gray800 }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3 deck-scroll">
              {drawerComments.length > 0 ? drawerComments.map((c) => (
                <div key={c.id} className="group rounded-2xl p-4 transition-all duration-300"
                  style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}` }}>
                  {c.author_name && <p className="text-xs font-bold mb-1" style={{ color: Z.white }}>{c.author_name}</p>}
                  <p className="text-sm leading-relaxed" style={{ color: Z.gray400 }}>{c.comment}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px]" style={{ color: Z.gray600 }}>
                      {c.created_at && new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    {!is_decided && (
                      <button onClick={() => handleDeleteComment(c.id)} disabled={deletingComment === c.id}
                        className="opacity-0 group-hover:opacity-100 transition-all" style={{ color: Z.coral }}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )) : (
                <p className="text-sm text-center py-10" style={{ color: Z.gray600 }}>
                  {is_decided ? 'No comments on this section.' : 'No comments yet. Add one below.'}
                </p>
              )}
            </div>

            {!is_decided && !isInactive && commentDrawerSection && (
              <div className="border-t p-5" style={{ borderColor: Z.borderSubtle }}>
                <textarea
                  value={commentInputs[commentDrawerSection] || ''}
                  onChange={(e) => setCommentInputs((p) => ({ ...p, [commentDrawerSection]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(commentDrawerSection); } }}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full rounded-xl px-4 py-3 text-sm resize-none focus:outline-none transition-all"
                  style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}`, color: Z.white }}
                  onFocus={(e) => { e.target.style.borderColor = Z.gold; e.target.style.boxShadow = '0 0 0 3px rgba(212,168,67,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  onClick={() => handleAddComment(commentDrawerSection)}
                  disabled={!(commentInputs[commentDrawerSection] || '').trim() || addingComment === commentDrawerSection}
                  className="mt-3 w-full py-2.5 text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-30 transition-all"
                  style={{ background: Z.gold, color: '#000000', border: `1px solid ${Z.goldLight}` }}>
                  {addingComment === commentDrawerSection ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LiveKit Audio Presenter Panel */}
      {presenterActive && (
        <LiveKitAudioPanel
          isActive={presenterActive}
          hook={livekit}
          onClose={() => { livekit.disconnect(); setPresenterActive(false); }}
          onSlideAdvance={(section) => goToSlide(section, totalSlides, false, true)}
          avatarName={livekit.avatarName || data?.avatar_name}
          avatarImageUrl={data?.avatar_image_url}
        />
      )}

      {/* Floating AI button with expandable menu */}
      {emailVerified && data && !chatOpen && !presenterActive && !isInactive && (
        <div className="fixed bottom-6 right-6 z-40">
          {/* Expanded menu — shows above the button */}
          {aiMenuOpen && (
            <>
              <div className="fixed inset-0" onClick={() => setAiMenuOpen(false)} />
              <div className="absolute bottom-16 right-0 flex flex-col items-end gap-3 mb-2">
                {/* Ask AI — circular icon button */}
                <button
                  onClick={() => { setAiMenuOpen(false); setChatOpen(true); }}
                  className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{
                    background: 'rgba(0,0,0,0.80)',
                    backdropFilter: 'blur(24px)',
                    border: `1px solid ${Z.border}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                  }}
                  title="Ask AI"
                >
                  <svg className="w-6 h-6" style={{ color: Z.goldLight }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                </button>

                {/* Present — circular icon button with avatar image */}
                {data.livekit_enabled === true && (
                  <button
                    onClick={() => { setAiMenuOpen(false); setPresenterActive(true); livekit.connect(token); }}
                    className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 overflow-hidden"
                    style={{
                      background: 'rgba(0,0,0,0.80)',
                      backdropFilter: 'blur(24px)',
                      border: `1px solid ${Z.gold}`,
                      boxShadow: '0 4px 20px rgba(212,168,67,0.3)',
                    }}
                    title="Present Proposal"
                  >
                    {data.avatar_image_url ? (
                      <img src={data.avatar_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-6 h-6" style={{ color: Z.goldLight }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </>
          )}

          {/* AI icon button — dims when menu is open */}
          <button
            onClick={() => setAiMenuOpen(!aiMenuOpen)}
            className="w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: aiMenuOpen
                ? 'rgba(100,100,100,0.5)'
                : `linear-gradient(135deg, ${Z.gold}, ${Z.goldDark})`,
              boxShadow: aiMenuOpen
                ? '0 2px 10px rgba(0,0,0,0.2)'
                : '0 4px 20px rgba(212,168,67,0.3)',
              opacity: aiMenuOpen ? 0.6 : 1,
              transition: 'all 0.2s ease',
            }}
            title="AI Assistant"
          >
            <svg className="w-6 h-6" fill="none" stroke={aiMenuOpen ? '#fff' : '#000'} strokeWidth={2} viewBox="0 0 24 24">
              {aiMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              )}
            </svg>
          </button>
        </div>
      )}

      {/* Ask AI chat panel */}
      {emailVerified && data && !isInactive && (
        <ProposalChatPanel
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          token={token}
        />
      )}

      {/* Decision Dialog */}
      {showDecisionDialog && !isInactive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setShowDecisionDialog(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative rounded-[26px] max-w-md w-full p-6 overflow-hidden"
            style={{ background: Z.bgCard, border: `1px solid ${Z.borderSubtle}`, animation: 'menuFadeIn 0.2s ease-out', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Gold top accent */}
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${Z.gold}, transparent)` }} />

            <h3 className="text-xl font-bold mb-1" style={{ color: Z.white }}>
              {showDecisionDialog === 'approve' ? 'Accept Proposal' : 'Send Back for Changes'}
            </h3>
            <p className="text-sm mb-5" style={{ color: Z.gray400 }}>
              {showDecisionDialog === 'approve'
                ? 'Please provide any final comments or feedback along with your approval.'
                : 'Please describe the changes you\'d like us to make.'}
            </p>
            <textarea
              value={decisionComment}
              onChange={(e) => setDecisionComment(e.target.value)}
              rows={4}
              placeholder="Your feedback (minimum 20 characters)..."
              className="w-full rounded-xl px-4 py-3 text-sm resize-none focus:outline-none transition-all"
              style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}`, color: Z.white }}
              onFocus={(e) => { e.target.style.borderColor = Z.gold; e.target.style.boxShadow = '0 0 0 3px rgba(212,168,67,0.12)'; }}
              onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'none'; }}
            />
            <p className="text-xs mt-1.5 mb-5" style={{ color: Z.gray600 }}>
              {decisionComment.trim().length}/20 characters minimum
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowDecisionDialog(null)}
                className="flex-1 py-3 text-sm font-medium rounded-xl transition-colors"
                style={{ color: Z.gray400, background: Z.gray800, border: `1px solid ${Z.borderSubtle}` }}>
                Cancel
              </button>
              <button onClick={() => handleDecision(showDecisionDialog)}
                disabled={decisionComment.trim().length < 20 || submittingDecision}
                className="flex-1 py-3 text-sm font-semibold rounded-xl transition-all disabled:opacity-30"
                style={{
                  background: showDecisionDialog === 'approve' ? Z.gold : Z.amber,
                  color: '#000000',
                  border: showDecisionDialog === 'approve' ? `1px solid ${Z.goldLight}` : '1px solid transparent',
                }}>
                {submittingDecision ? 'Submitting...' : showDecisionDialog === 'approve' ? 'Confirm Acceptance' : 'Confirm Send Back'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Keyboard Nav ── */

const KeyboardNav = ({ currentSlide, totalSlides, goToSlide }) => {
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goToSlide(currentSlide + 1, totalSlides); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); goToSlide(currentSlide - 1, totalSlides); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [currentSlide, totalSlides, goToSlide]);
  return null;
};

export default ClientProposalReviewPage;
